import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { formatNumber } from '@angular/common';
import { Product, Price } from '@models/product';
import { CartService } from '@services/cart.service';
import { Stock, Depot } from '@models/stock';
import { StockService } from '@services/stock.service';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TrackingService } from 'src/app/services/tracking.service';

@Component({
  selector: 'gob-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent implements OnInit {
  _product: Product;
  priceSelected: Price;
  now: Date;
  today: Date;
  later: Date;
  releaseDate: Date;
  stocks: Stock[]=[];
  depots: Depot[]=[];
  mainDepot?: Depot;
  depotsWithNoStock?: Depot[]=[];
  isAlertOpen: boolean = false;

  @Input()
  set product(val: Product){
    this._product=val;
    if(val.prices.length){
      // Check the current price according to current date
      this.priceSelected=val.prices[0];
      if (val.prices.length > 1) {
        let startDate = new Date(val.prices[1].start);
        let endDate = new Date(val.prices[1].end);
        if (startDate <= new Date() && new Date() <= endDate) {
          this.priceSelected = val.prices[1];
        } else {
          this.priceSelected = val.prices[0];
        }
      }

      // Retrieve product price ident
      let priceParam=this.router.snapshot.queryParamMap.get('price');
      if (!priceParam) {
        val.prices.slice(1).forEach((prices: { ident: any; }) => {
          if (prices.ident) {
            let price=val.prices.find((p: { ident: any; }) => p.ident == prices.ident);
            if(price){
              this.product.images=[...this.product.images, {name: price.img, ident: 'cover', isMain: 'false'}];
            }
          }
        });
      } else {
        if(priceParam){
          let price=val.prices.find((p: { ident: string; }) => p.ident==priceParam);
          if(price){
            this.priceSelected=price;
            this.product.images=[{name: price.img, ident: 'cover', isMain: 'false'}, ...this.product.images];
          }
        }
      }
      // Load depots for delivery options
      this.loadDepots(val.ident);
      // Load release date
      this.releaseDate=new Date(val.releaseDate);
    }
  }

  get product(){
    return this._product;
  }

  constructor(
    private router: ActivatedRoute,
    public cart: CartService,
    private tracking: TrackingService,
    private stockServ: StockService) { }

  ngOnInit(): void {
    if (this.product) {
      this.tracking.trackMeta('ViewContent', {
        content_ids: [this.product.ref],
        content_name: this.product.name,
        content_type: 'product',
        value: this.priceSelected,
        currency: 'EUR'
      });
    }
  }

/**
 * Retrieves stock information from the stock service for the specified product ID and updates the local depots list.
 * Logs the raw response and the parsed depots to the console.
 *
 * @param productId - The identifier of the product for which depots are being fetched.
 */
  private loadDepots(productId: string) {
    this.stockServ.getStocks(productId).subscribe((response: { data?: Stock[] } | Stock[]) => {
      // Today's date
      const now = new Date();
      const noon = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
      this.today = (now < noon) ? now : new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

      this.stocks = [];
      this.stocks = (Array.isArray(response) ? response : response.data).filter(stock => stock.qte > 0);
      /* console.log('Stocks response:', this.stocks); */

      if (!this.stocks.length) {
        return;
      }

      // Prepare observables for depot details
      const depotRequests: Observable<Depot | null>[] = this.stocks.map(stock =>
        this.stockServ.getDepot(stock.depot).pipe(
          catchError(() => of(null)) // Prevent a single failure from breaking all
        )
      );

      forkJoin(depotRequests).subscribe(results => {
        /* console.log('Depot details results:', results); */

        const mainDepot = results.find((depot: Depot | null): depot is Depot => depot?.isMain === true);
        if (mainDepot) {
          /* console.log('Main depot found:', mainDepot); */
          this.mainDepot = mainDepot;

          this.depots = results.filter(dep => dep?.ident !== mainDepot.ident) as Depot[];
        } else {
          // No main depot, add 4 days
          this.today = this.setDate(4);
        }
        // Returns all shops for click & collect which have stock except the main depot
        this.depots = results.filter(dep => dep?.ident !== this.mainDepot?.ident) as Depot[];

        // Returns all other shops for click & collect which have no stock
        const depotsWithNoStock: Observable<Depot[]>[] = this.stocks.map(() =>
          this.stockServ.getDepots().pipe(
            map(depots => depots.filter((depot: { isMain: any; }) => !depot.isMain)), // exclude main depot
            catchError(() => of([])) // return empty array on error
          )
        );
        forkJoin(depotsWithNoStock).subscribe(results => {
          // results is an array of arrays, so flatten them into a single array
          const allDepots: Depot[] = ([] as Depot[]).concat(...results);
          // Optionally, remove duplicates by ident
          const uniqueDepots = allDepots.filter(
            (depot: { ident: any; }, index: any, self: any[]) =>
              index === self.findIndex(d => d.ident === depot.ident)
          );
          /* console.log('All depots:', uniqueDepots); */

          // Find depots missing from this.depots
          const missingDepots = uniqueDepots.filter(
            (depot: { ident: any; }) => !this.depots.some(existing => existing.ident === depot.ident)
          );
          /* console.log('Missing depots:', missingDepots); */

          this.depotsWithNoStock = missingDepots;

          this.later = this.setDate(4);
        });
        /* console.log('Depot with no stock results:', this.depotsWithNoStock); */
      });
    });
  }

/**
 * Adjusts the given date to skip weekends.
 * If the date falls on a Sunday, it is advanced to the following Monday.
 * If the date falls on a Saturday, it is advanced to the following Monday.
 *
 * @param date - The date object to be adjusted
 */
  setDate(addDays: number = 0, date: string = null) {
    const now = (date) ? new Date(date) : new Date();

    // Add days if any
    if (addDays > 0) {
      now.setDate(now.getDate() + addDays);
      if (now.getDay() === 0) {
        now.setDate(now.getDate() + 1); // Sunday to Monday
      }
      if (now.getDay() === 6) {
        now.setDate(now.getDate() + 2); // Saturday to Monday
      }
    } else {
      // Sunday
      if (now.getDay() === 0) {
        now.setDate(now.getDate() + 1); // Sunday to Monday
      }

      // Saturday
      if (now.getDay() === 6) {
        now.setDate(now.getDate() + 2); // Saturday to Monday
      }
    }

    return now;
  }

  getTags(){
    let result=this.product.tags[0].name;
    this.product.tags.forEach((tag: { name: string; }, i: number) => {
      if(i!=0)
        result+=', '+tag.name;
    });
    return result;
  }

  setPrice(ident: string){
    this.priceSelected=this.product.prices.find((p: { ident: string; }) => p.ident==ident);
  }

  /**
   * CORRECTION : Bloque l'incrémentation selon le stock réel ou la limite préco
   */
  add(input: any) {
    let qte: number = parseInt(input.value);
    const max = this.cart.maxQte(this.product, this.priceSelected);

    if (qte < max) {
      qte++;
      input.value = qte;
    }
  }

  minus(input: any) {
    let qte: number = parseInt(input.value); 
    if (qte > 1) {
      qte--;
      input.value = qte;
    }
  }

  isLimited(product: Product){
    if(product.limitOrder > 0) return true;
    return false;
  }

  /**
   * Add product to cart with quantity
   * @param product added product
   * @param priceSelected selected price
   * @param qte added quantity
   */
  addCart(product: Product, priceSelected: Price, qte: any){
    this.cart.add(product, priceSelected, parseInt(qte));

    // Facebook Pixel custom event
    this.tracking.trackMeta('AddToCart', {
      content_ids: [this.product.ref],
      content_name: this.product.name,
      content_type: 'product',
      value: this.priceSelected,
      currency: 'EUR'
    });

    // Google Ads
    this.tracking.trackAddToCart(
      Number(this.priceSelected)
    );
  }
  scrollToReviews() {
      const reviewSection = document.getElementById('review-tab-pane');
      if (reviewSection) {
        const headerOffset = 220; 
        const elementPosition = reviewSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
  }
  /**
   * Vérifie si la quantité actuelle est au minimum (1)
   */
  isMinQte(val: string | number): boolean {
    return parseInt(val.toString()) <= 1;
  }

  /**
   * Vérifie si la quantité actuelle a atteint le stock max
   */
  isMaxQte(val: string | number): boolean {
    const current = parseInt(val.toString());
    const max = this.cart.maxQte(this.product, this.priceSelected);
    return current >= max;
  }

  openAlertModal() {
    this.isAlertOpen = true;
  }
}
